import { createRouter, createWebHistory } from "vue-router";
import store from "@/store";
import LoadingManager from "@/services/loadingManager";

const routes = [
  {
    path: "/landing",
    name: "Landing",
    component: () => import("../views/LandingPage.vue"),
    meta: { public: true },
  },
  {
    path: "/login",
    name: "Login",
    component: () => import("../views/LoginView.vue"),
    meta: { public: true },
  },
  {
    path: "/",
    component: () => import("../layouts/MainLayout.vue"),
    redirect: "/dashboard",
    children: [
      // Dashboard
      {
        path: "dashboard",
        name: "Dashboard",
        component: () => import("../modules/dashboard/DashboardPage.vue"),
      },
      // Profile
      {
        path: "profile",
        name: "UserProfile",
        component: () => import("../views/UserProfileView.vue"),
      },
      // Customers
      {
        path: "customers",
        name: "Customers",
        component: () => import("../modules/customers/views/CustomerList.vue"),
        meta: { permission: "customers:view" },
      },
      {
        path: "customer-uploads",
        name: "CustomerUploads",
        component: () =>
          import("../modules/customers/views/CustomerUploadView.vue"),
        meta: { permission: "customers:view" },
      },
      {
        path: "customers/upload",
        name: "CustomerUploadsAlias",
        component: () =>
          import("../modules/customers/views/CustomerUploadView.vue"),
        meta: { permission: "customers:view" },
      },
      {
        path: "customers/:id",
        name: "CustomerDetail",
        component: () =>
          import("../modules/customers/views/CustomerDetail.vue"),
        meta: {
          permission: "customers:view",
          breadcrumb: [
            { label: 'Customers', to: '/customers' },
            { label: 'Customer Detail' },
          ],
        },
      },
      // Invoices
      {
        path: "invoices",
        name: "Invoices",
        component: () => import("../modules/invoices/InvoiceList.vue"),
        meta: { permission: "invoices:view" },
      },
      {
        path: "invoices/new",
        name: "InvoiceCreate",
        component: () => import("../modules/invoices/InvoiceForm.vue"),
        meta: {
          permission: "invoices:create",
          breadcrumb: [
            { label: 'Invoices', to: '/invoices' },
            { label: 'New Invoice' },
          ],
        },
      },
      {
        path: "invoices/:id/edit",
        name: "InvoiceEdit",
        component: () => import("../modules/invoices/InvoiceForm.vue"),
        meta: {
          permission: "invoices:edit",
          breadcrumb: [
            { label: 'Invoices', to: '/invoices' },
            { label: 'Edit Invoice' },
          ],
        },
      },
      {
        path: "invoices/:id",
        name: "InvoiceDetail",
        component: () => import("../modules/invoices/InvoiceDetail.vue"),
        meta: {
          permission: "invoices:view",
          breadcrumb: [
            { label: 'Invoices', to: '/invoices' },
            { label: 'Invoice Detail' },
          ],
        },
      },
      {
        path: "invoice-uploads",
        name: "InvoiceUploads",
        component: () => import("../modules/invoices/InvoiceUploadView.vue"),
        meta: {
          permission: "invoice_upload:view",
          breadcrumb: [
            { label: 'Invoices', to: '/invoices' },
            { label: 'Upload Invoices' },
          ],
        },
      },
      // Payments
      {
        path: "payments",
        name: "Payments",
        component: () => import("../modules/payments/PaymentList.vue"),
        meta: { permission: "payments:view" },
      },
      // [CMS-DEPRECATED] Payment create route deprecated per CMS Change Request 2026-06-07
      // New collections entries must be created via the Receipts module.
      // {
      //   path: "payments/new",
      //   name: "PaymentCreate",
      //   component: () => import("../modules/payments/PaymentForm.vue"),
      //   meta: {
      //     permission: "payments:create",
      //     breadcrumb: [
      //       { label: 'Payments', to: '/payments' },
      //       { label: 'New Payment' },
      //     ],
      //   },
      // },
      // Receipts
      {
        path: "receipts",
        name: "Receipts",
        component: () => import("../modules/receipts/ReceiptList.vue"),
        meta: { permission: "receipts:view" },
      },
      {
        path: "receipts/new",
        name: "ReceiptCreate",
        component: () => import("../modules/receipts/ReceiptForm.vue"),
        meta: {
          permission: "receipts:create",
          breadcrumb: [
            { label: 'Receipts', to: '/receipts' },
            { label: 'New Receipt' },
          ],
        },
      },
      {
        path: "receipt-uploads",
        name: "ReceiptUploads",
        component: () => import("../modules/receipts/ReceiptUploadView.vue"),
        meta: {
          permission: "receipts:create",
          breadcrumb: [
            { label: 'Receipts', to: '/receipts' },
            { label: 'Upload Receipts' },
          ],
        },
      },
      {
        path: "receipts/:id",
        name: "ReceiptDetail",
        component: () => import("../modules/receipts/ReceiptDetail.vue"),
        meta: {
          permission: "receipts:view",
          breadcrumb: [
            { label: 'Receipts', to: '/receipts' },
            { label: 'Receipt Detail' },
          ],
        },
      },
      // BRD v2 (Section 10): 3-Panel Receipt Allocation View
      {
        path: "receivables/receipt-allocation",
        name: "ReceiptAllocation",
        component: () => import("../views/ReceiptAllocationView.vue"),
        meta: {
          permission: "receipts:create",
          breadcrumb: [
            { label: 'Receivables' },
            { label: 'Receipt Allocation' },
          ],
        },
      },
      // BRD v2 (Section 5): Billing Summary Dashboard & Revenue
      {
        path: "dashboards/billing",
        name: "BillingDashboard",
        component: () => import("../views/BillingDashboardView.vue"),
        meta: { permission: "dashboard:view" },
      },
      {
        path: "revenue/total",
        name: "TotalRevenue",
        component: () => import("../views/RevenueRegisterView.vue"),
        meta: {
          permission: "dashboard:view",
          breadcrumb: [{ label: "Revenue" }, { label: "Billed Revenue" }],
        },
      },
      {
        path: "revenue/unbilled",
        name: "UnbilledRevenue",
        component: () => import("../views/RevenueRegisterView.vue"),
        meta: {
          permission: "dashboard:view",
          breadcrumb: [{ label: "Revenue" }, { label: "Unbilled Revenue" }],
        },
      },
      // BRD v2 (Section 6): Outstanding Follow-up Dashboard
      {
        path: "dashboards/outstanding-followup",
        name: "OutstandingFollowupDashboard",
        component: () => import("../views/OutstandingFollowupDashboardView.vue"),
        meta: {
          permission: "invoices:view",
          breadcrumb: [
            { label: "Receivables Management" },
            { label: "Outstanding Followup" },
          ],
        },
      },
      {
        path: "outstanding-followup",
        name: "OutstandingFollowup",
        component: () => import("../views/OutstandingFollowupDashboardView.vue"),
        meta: {
          permission: "invoices:view",
          breadcrumb: [
            { label: "Receivables Management" },
            { label: "Outstanding Followup" },
          ],
        },
      },
      // Finance / Knock-off
      {
        path: "finance/knockoff/:receiptId",
        name: "KnockoffView",
        component: () => import("../modules/finance/KnockoffView.vue"),
        meta: { permission: "payments:create" },
      },
      {
        path: "knockoff-uploads",
        name: "KnockoffUploads",
        component: () => import("../modules/finance/KnockoffUploadView.vue"),
        meta: {
          permission: "knockoff_upload:view",
          breadcrumb: [
            { label: 'Finance', to: '/receipts' },
            { label: 'Upload Knock-offs' },
          ],
        },
      },
      // Collections
      {
        path: "collections",
        name: "Collections",
        component: () => import("../views/CollectionsView.vue"),
        meta: { permission: "collections:view" },
      },
      // Orders
      {
        path: "orders",
        name: "Orders",
        component: () => import("../views/OrdersView.vue"),
        meta: { permission: "orders:view" },
      },
      // Sales Orders
      {
        path: "sales-orders",
        name: "SalesOrders",
        component: () => import("../modules/salesOrders/SalesOrdersView.vue"),
        meta: { permission: "salesOrders:view" },
      },
      // Delivery Challans
      {
        path: "deliveries",
        name: "Deliveries",
        component: () => import("../views/DeliveriesView.vue"),
        meta: { permission: "delivery:view" },
      },
      // Reports
      {
        path: "reports",
        name: "Reports",
        component: () => import("../views/ReportsView.vue"),
        meta: { permission: "reports:view" },
      },
      // Followups
      {
        path: "followups",
        name: "Followups",
        component: () => import("../modules/followups/FollowupList.vue"),
        meta: { permission: "followups:view" },
      },
      // Reminders
      {
        path: "reminders",
        name: "Reminders",
        component: () => import("../modules/reminders/ReminderList.vue"),
        meta: { permission: "reminders:view" },
      },
      // Workflow
      {
        path: "workflow",
        name: "Workflow",
        component: () => import("../modules/workflow/WorkflowList.vue"),
        meta: { permission: "workflow:view" },
      },
      {
        path: "approval-logs",
        name: "ApprovalLogs",
        component: () => import("../modules/workflow/ApprovalLogs.vue"),
        meta: { permission: "workflow:logs:view" },
      },
      // Notifications
      {
        path: "notifications",
        name: "Notifications",
        component: () =>
          import("../modules/notifications/NotificationList.vue"),
      },
      // Settings
      {
        path: "settings",
        name: "SettingsHome",
        component: () => import("../views/SettingsHomeView.vue"),
        meta: { breadcrumb: [{ label: "Home", to: "/dashboard" }, { label: "Settings Home" }] },
      },
      {
        path: "settings/users",
        name: "Users",
        component: () => import("../views/UsersView.vue"),
        meta: { permission: "users:view", breadcrumb: [{ label: "Home", to: "/dashboard" }, { label: "Settings Home", to: "/settings" }, { label: "Users" }] },
      },
      {
        path: "settings/roles",
        name: "Roles",
        component: () => import("../views/RolesView.vue"),
        meta: { permission: "roles:manage", breadcrumb: [{ label: "Home", to: "/dashboard" }, { label: "Settings Home", to: "/settings" }, { label: "Roles and permissions" }] },
      },
      {
        path: "settings/security",
        name: "SecurityDashboard",
        component: () => import("../views/SecurityDashboardView.vue"),
        meta: { permission: "reports:view" },
      },
      
      // Flat settings route definitions
      {
        path: "settings/master-data",
        redirect: "/settings/legal-entities",
      },
      {
        path: "settings/legal-entities",
        name: "LegalEntities",
        component: () => import("../modules/master/views/LegalEntitiesView.vue"),
        meta: { permission: "organization:legal_entities:view", breadcrumb: [{ label: "Home", to: "/dashboard" }, { label: "Settings Home", to: "/settings" }, { label: "Legal entities" }] },
      },
      {
        path: "settings/bu-groups",
        name: "BuGroups",
        component: () => import("../components/ComingSoon.vue"),
        props: { title: "BU Groups", icon: "Layers3" },
        meta: { permission: "organization:bu_groups:view" },
      },
      {
        path: "settings/business-units",
        name: "BusinessUnits",
        component: () => import("../modules/master/views/BusinessUnitsView.vue"),
        meta: { permission: "organization:business_units:view" },
      },
      {
        path: "settings/currencies",
        name: "CurrencyMaster",
        component: () => import("../modules/master/views/MasterRegistryView.vue"),
        props: () => ({ config: require("../modules/master/config/currency.config").default }),
        meta: { permission: "financial:currencies:view" },
      },
      {
        path: "settings/countries",
        name: "CountryMaster",
        component: () => import("../modules/master/views/MasterRegistryView.vue"),
        props: () => ({ config: require("../modules/master/config/country.config").default }),
        meta: { permission: "geography:countries:view" },
      },
      {
        path: "settings/states",
        name: "StateMaster",
        component: () => import("../modules/master/views/MasterRegistryView.vue"),
        props: () => ({ config: require("../modules/master/config/state.config").default }),
        meta: { permission: "geography:states:view" },
      },
      {
        path: "settings/cities",
        name: "CityMaster",
        component: () => import("../modules/master/views/MasterRegistryView.vue"),
        props: () => ({ config: require("../modules/master/config/city.config").default }),
        meta: { permission: "geography:cities:view" },
      },
      {
        path: "settings/regions",
        name: "RegionMaster",
        component: () => import("../modules/master/views/MasterRegistryView.vue"),
        props: () => ({ config: require("../modules/master/config/region.config").default }),
        meta: { permission: "geography:regions:view" },
      },
      {
        path: "settings/exchange-rates",
        name: "ExchangeRates",
        component: () => import("../modules/master/views/MasterRegistryView.vue"),
        props: () => ({ config: require("../modules/master/config/exchangeRates.config").default }),
        meta: { permission: "financial:exchange_rates:view" },
      },
      {
        path: "settings/payment-terms",
        name: "PaymentTerms",
        component: () => import("../modules/master/views/MasterRegistryView.vue"),
        props: () => ({ config: require("../modules/master/config/paymentTerms.config").default }),
        meta: { permission: "financial:payment_terms:view" },
      },
      {
        path: "settings/tax-codes",
        name: "TaxCodes",
        component: () => import("../modules/master/views/MasterRegistryView.vue"),
        props: () => ({ config: require("../modules/master/config/taxCodes.config").default }),
        meta: { permission: "financial:tax_codes:view" },
      },
      {
        path: "settings/bank-accounts",
        name: "BankAccountMaster",
        component: () => import("../modules/master/views/BankAccountsView.vue"),
        meta: { permission: "bank_accounts:view" },
      },
      {
        path: "settings/number-series",
        name: "DocNumberSeries",
        component: () => import("../modules/master/views/MasterRegistryView.vue"),
        props: () => ({ config: require("../modules/master/config/numberSeries.config").default }),
        meta: { permission: "system:number_series:view" },
      },
      {
        path: "settings/fiscal-years",
        name: "FiscalYears",
        component: () => import("../modules/master/views/MasterRegistryView.vue"),
        props: () => ({ config: require("../modules/master/config/fiscalYears.config").default }),
        meta: { permission: "system:fiscal_years:view" },
      },
      {
        path: "settings/holiday-calendar",
        name: "HolidayCalendar",
        component: () => import("../modules/master/views/MasterRegistryView.vue"),
        props: () => ({ config: require("../modules/master/config/holidayCalendar.config").default }),
        meta: { permission: "system:holiday_calendar:view" },
      },
      {
        path: "settings/dynamic-fields",
        name: "DynamicObjectManager",
        component: () => import("../modules/master/views/DynamicObjectManagerView.vue"),
        meta: { permission: "metadata:edit", breadcrumb: [{ label: "Home", to: "/dashboard" }, { label: "Settings Home", to: "/settings" }, { label: "Dynamic object manager" }] },
      },
      {
        path: "settings/period-locks",
        name: "PeriodLockManager",
        component: () => import("../modules/master/views/PeriodLockManagerView.vue"),
        meta: { permission: "periodLock:view", breadcrumb: [{ label: "Home", to: "/dashboard" }, { label: "Settings Home", to: "/settings" }, { label: "Period locks" }] },
      },
      {
        path: "settings/email-templates",
        name: "EmailTemplatesView",
        component: () => import("../modules/master/views/EmailTemplatesView.vue"),
        meta: { permission: "master:view", breadcrumb: [{ label: "Home", to: "/dashboard" }, { label: "Settings Home", to: "/settings" }, { label: "Email templates" }] },
      },
      {
        path: "settings/email-alerts",
        name: "EmailAlertsView",
        component: () => import("../modules/master/views/EmailAlertsView.vue"),
        meta: { permission: "master:view", breadcrumb: [{ label: "Home", to: "/dashboard" }, { label: "Settings Home", to: "/settings" }, { label: "Email alerts" }] },
      },

      // Workflow & Approvals Settings
      {
        path: "settings/workflow",
        name: "WorkflowAdminHome",
        component: () => import("../modules/workflow-admin/WorkflowAdminHome.vue"),
        meta: { permission: "workflow:admin", breadcrumb: [{ label: "Home", to: "/dashboard" }, { label: "Settings Home", to: "/settings" }, { label: "Workflow and approvals" }] },
      },
      {
        path: "settings/workflow/designer",
        name: "WorkflowDesignerList",
        component: () => import("../modules/workflow-admin/WorkflowDesignerList.vue"),
        meta: { permission: "workflow:designer:manage", breadcrumb: [{ label: "Home", to: "/dashboard" }, { label: "Settings Home", to: "/settings" }, { label: "Workflow and approvals", to: "/settings/workflow" }, { label: "Workflow designer" }] },
      },
      {
        path: "settings/workflow/designer/new",
        name: "WorkflowDesignerCreate",
        component: () => import("../modules/workflow-admin/WorkflowDesignerWizard.vue"),
        meta: { permission: "workflow:designer:manage", breadcrumb: [{ label: "Home", to: "/dashboard" }, { label: "Settings Home", to: "/settings" }, { label: "Workflow designer", to: "/settings/workflow/designer" }, { label: "New workflow" }] },
      },
      {
        path: "settings/workflow/designer/:id",
        name: "WorkflowDesignerEdit",
        component: () => import("../modules/workflow-admin/WorkflowDesignerWizard.vue"),
        meta: { permission: "workflow:designer:manage", breadcrumb: [{ label: "Home", to: "/dashboard" }, { label: "Settings Home", to: "/settings" }, { label: "Workflow designer", to: "/settings/workflow/designer" }, { label: "Edit workflow" }] },
      },
      {
        path: "settings/workflow/matrix",
        name: "ApprovalMatrixList",
        component: () => import("../modules/workflow-admin/ApprovalMatrixView.vue"),
        meta: { permission: "workflow:matrix:manage", breadcrumb: [{ label: "Home", to: "/dashboard" }, { label: "Settings Home", to: "/settings" }, { label: "Workflow and approvals", to: "/settings/workflow" }, { label: "Approval matrix" }] },
      },
      {
        path: "settings/workflow/delegation",
        name: "ApprovalDelegationList",
        component: () => import("../modules/workflow-admin/DelegationView.vue"),
        meta: { permission: "workflow:delegation:manage", breadcrumb: [{ label: "Home", to: "/dashboard" }, { label: "Settings Home", to: "/settings" }, { label: "Workflow and approvals", to: "/settings/workflow" }, { label: "Approval substitutes" }] },
      },
      {
        path: "settings/workflow/escalation",
        name: "EscalationRulesList",
        component: () => import("../modules/workflow-admin/EscalationRulesView.vue"),
        meta: { permission: "workflow:admin", breadcrumb: [{ label: "Home", to: "/dashboard" }, { label: "Settings Home", to: "/settings" }, { label: "Workflow and approvals", to: "/settings/workflow" }, { label: "Escalation rules" }] },
      },
      {
        path: "settings/workflow/sla",
        name: "SLAConfiguration",
        component: () => import("../modules/workflow-admin/EscalationRulesView.vue"),
        meta: { permission: "workflow:admin", breadcrumb: [{ label: "Home", to: "/dashboard" }, { label: "Settings Home", to: "/settings" }, { label: "Workflow and approvals", to: "/settings/workflow" }, { label: "SLA configuration" }] },
      },
      {
        path: "settings/workflow/templates",
        name: "NotificationTemplatesList",
        component: () => import("../modules/workflow-admin/NotificationTemplatesView.vue"),
        meta: { permission: "workflow:templates:manage", breadcrumb: [{ label: "Home", to: "/dashboard" }, { label: "Settings Home", to: "/settings" }, { label: "Workflow and approvals", to: "/settings/workflow" }, { label: "Notification templates" }] },
      },
      {
        path: "settings/workflow/tracking",
        name: "ApprovalTrackingList",
        component: () => import("../modules/workflow-admin/ApprovalTrackingView.vue"),
        meta: { permission: "workflow:tracking:view", breadcrumb: [{ label: "Home", to: "/dashboard" }, { label: "Settings Home", to: "/settings" }, { label: "Workflow and approvals", to: "/settings/workflow" }, { label: "Approval tracking" }] },
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach((to, from, next) => {
  const isAuthenticated = store.getters["auth/isAuthenticated"];

  if (to.meta.public) {
    // Allow Login page even for authenticated users who navigate there explicitly
    if (isAuthenticated && to.name !== "Login") return next({ name: "Dashboard" });
    return next();
  }

  if (!isAuthenticated) {
    return next({ name: "Landing" });
  }

  const requiredPermission = to.meta.permission;
  if (requiredPermission) {
    const hasPerm = store.getters["auth/hasPermission"](requiredPermission);
    if (!hasPerm) {
      // Prevent infinite loops if fallback lacks permission
      if (to.name === "Dashboard") return next("/");
      return next({ name: "Dashboard" });
    }
  }

  // Start route-level progress indicator for authenticated navigation
  LoadingManager.onRouteStart();
  next();
});

// End route progress and dismiss AppLoader after every navigation
router.afterEach(() => {
  LoadingManager.onRouteEnd();
  LoadingManager.hideAppLoader();
});

// Ensure loading state is cleared even on navigation errors
router.onError(() => {
  LoadingManager.onRouteEnd();
  LoadingManager.hideAppLoader();
});

export default router;
